// Phone + device create/edit dialogs, the owner select, and the pickers the
// account / profile forms use. A picker lists registered records and ends
// with "+ New …", which opens the create dialog in place and selects the new
// record when it is saved.
//
// Since mig 0020 the device is edited inside the phone form: the phone's
// Device section picks no device, an existing one (whose fields can be
// edited there) or a new one. The same DeviceFields section is used by the
// stand-alone device dialog.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Field, selectClass, testerLabel } from '@/components/ui/field';
import { ApiError, devices, lookups, phoneNumbers, testers } from '@/lib/api';
import {
  DEVICE_IDENTIFIER_MAX,
  DEVICE_TEXT_MAX,
  FRT_POSSESSION_LABEL,
  PHONE_LINE_TYPES,
  isValidCountryCode,
  isValidPhoneNumber,
} from '@frt/shared-types';
import type {
  DeviceRow,
  DeviceUpsert,
  PhoneLineType,
  PhoneNumberDetail,
  PhoneNumberRow,
  PhoneNumberUpsert,
} from '@frt/shared-types';

const NEW = '__new__';

// ---------------------------------------------------------------------------
// Owner (a phone's Subscriber, a device's Custodian): a tester or FRT
// ---------------------------------------------------------------------------

/** '' = not chosen, 'frt' = FRT (internal), otherwise `t:<tester id>`. */
export type OwnerValue = '' | 'frt' | `t:${number}`;

export function ownerValue(a: { owner_frt: boolean; owner_tester_id: number | null }): OwnerValue {
  return a.owner_frt ? 'frt' : a.owner_tester_id ? `t:${a.owner_tester_id}` : '';
}

export function ownerInput(v: OwnerValue): { owner_frt: boolean; owner_tester_id: number | null } {
  return {
    owner_frt: v === 'frt',
    owner_tester_id: v.startsWith('t:') ? Number(v.slice(2)) : null,
  };
}

export function OwnerSelect({
  value,
  onChange,
}: {
  value: OwnerValue;
  onChange: (v: OwnerValue) => void;
}) {
  const { data } = useQuery({
    queryKey: ['testers', { per_page: 500 }],
    queryFn: () => testers.list({ per_page: 500 }),
  });
  // Inactive testers stay listed only when already selected.
  const rows = (data?.data ?? []).filter(
    (t) => t.tester_status_name !== 'Inactive' || value === `t:${t.id}`,
  );
  return (
    <select value={value} onChange={(e) => onChange(e.target.value as OwnerValue)} className={selectClass}>
      <option value="">— select —</option>
      <option value="frt">{FRT_POSSESSION_LABEL}</option>
      {rows.map((t) => (
        <option key={t.id} value={`t:${t.id}`}>
          {testerLabel(t)}
        </option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------

const OPTION_CLIENTS = {
  'device-ownership-options': lookups.deviceOwnershipOptions,
  'phone-provider-options': lookups.phoneProviderOptions,
  'phone-payment-plan-options': lookups.phonePaymentPlanOptions,
  'phone-test-status-options': lookups.phoneTestStatusOptions,
  'phone-payment-responsibility-options': lookups.phonePaymentResponsibilityOptions,
  'phone-usage-status-options': lookups.phoneUsageStatusOptions,
  'phone-carrier-type-options': lookups.phoneCarrierTypeOptions,
  'device-manufacturer-options': lookups.deviceManufacturerOptions,
  'device-model-options': lookups.deviceModelOptions,
  'device-os-options': lookups.deviceOsOptions,
  'device-sim-restriction-options': lookups.deviceSimRestrictionOptions,
  'device-root-status-options': lookups.deviceRootStatusOptions,
} as const;
type OptionSlug = keyof typeof OPTION_CLIENTS;

function useOptions(slug: OptionSlug) {
  const { data } = useQuery({
    queryKey: ['lookups', slug],
    queryFn: () => OPTION_CLIENTS[slug].list(),
  });
  return data?.data ?? [];
}

function OptionSelect({
  slug,
  value,
  onChange,
  emptyLabel = '— select —',
}: {
  slug: OptionSlug;
  value: number;
  onChange: (v: number) => void;
  emptyLabel?: string;
}) {
  const options = useOptions(slug);
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))} className={selectClass}>
      <option value={0}>{emptyLabel}</option>
      {options
        .filter((o) => o.active || o.id === value)
        .map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
            {o.active ? '' : ' (inactive)'}
          </option>
        ))}
    </select>
  );
}

// The dialogs can open inside the account <form>; Enter in one of their
// inputs must not submit the account.
function blockEnterSubmit(e: React.KeyboardEvent) {
  if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') e.preventDefault();
}

function errorText(err: unknown): string | null {
  if (!err) return null;
  return err instanceof Error ? err.message : String(err);
}

// ---------------------------------------------------------------------------
// Device fields (shared by the device dialog and the phone form)
// ---------------------------------------------------------------------------

export interface DeviceDraft {
  device_identifier: string;
  manufacturer_id: number;
  model_id: number;
  os_id: number;
  sim_restriction_id: number;
  root_status_id: number;
  location_city: string;
  location_region: string;
  ownership_id: number;
  custodian: OwnerValue;
  notes: string;
}

export function blankDevice(): DeviceDraft {
  return {
    device_identifier: '',
    manufacturer_id: 0,
    model_id: 0,
    os_id: 0,
    sim_restriction_id: 0,
    root_status_id: 0,
    location_city: '',
    location_region: '',
    ownership_id: 0,
    custodian: '',
    notes: '',
  };
}

export function deviceDraftFrom(d: DeviceRow): DeviceDraft {
  return {
    // Converted LEGACY-* placeholders are meant to be replaced.
    device_identifier: d.device_identifier,
    manufacturer_id: d.manufacturer_id ?? 0,
    model_id: d.model_id ?? 0,
    os_id: d.os_id ?? 0,
    sim_restriction_id: d.sim_restriction_id ?? 0,
    root_status_id: d.root_status_id ?? 0,
    location_city: d.location_city ?? '',
    location_region: d.location_region ?? '',
    ownership_id: d.ownership_id ?? 0,
    custodian: ownerValue(d),
    notes: d.notes ?? '',
  };
}

export function deviceDraftErrors(d: DeviceDraft): Record<string, string> {
  const errs: Record<string, string> = {};
  if (!d.device_identifier.trim()) errs.device_identifier = 'Device ID is required';
  if (!d.manufacturer_id) errs.manufacturer_id = 'Select the manufacturer';
  if (!d.model_id) errs.model_id = 'Select the model';
  if (!d.ownership_id) errs.ownership_id = 'Select ownership';
  if (!d.custodian) errs.custodian = 'Select the device custodian';
  return errs;
}

export function deviceDraftToUpsert(d: DeviceDraft): DeviceUpsert {
  return {
    device_identifier: d.device_identifier.trim(),
    manufacturer_id: d.manufacturer_id,
    model_id: d.model_id,
    os_id: d.os_id || null,
    sim_restriction_id: d.sim_restriction_id || null,
    root_status_id: d.root_status_id || null,
    location_city: d.location_city.trim() || null,
    location_region: d.location_region.trim() || null,
    ownership_id: d.ownership_id,
    ...ownerInput(d.custodian),
    notes: d.notes.trim() || null,
  };
}

function DeviceFields({
  value,
  onChange,
  errors,
}: {
  value: DeviceDraft;
  onChange: (d: DeviceDraft) => void;
  errors: Record<string, string>;
}) {
  const set = (patch: Partial<DeviceDraft>) => onChange({ ...value, ...patch });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Device ID *" error={errors.device_identifier} className="sm:col-span-2">
        <Input
          maxLength={DEVICE_IDENTIFIER_MAX}
          value={value.device_identifier}
          placeholder="Serial, IMEI or asset tag"
          onChange={(e) => set({ device_identifier: e.target.value })}
          className="font-mono"
        />
      </Field>
      <Field label="Phone Manufacturer *" error={errors.manufacturer_id}>
        <OptionSelect slug="device-manufacturer-options" value={value.manufacturer_id} onChange={(v) => set({ manufacturer_id: v })} />
      </Field>
      <Field label="Phone Model *" error={errors.model_id}>
        <OptionSelect slug="device-model-options" value={value.model_id} onChange={(v) => set({ model_id: v })} />
      </Field>
      <Field label="Operating System">
        <OptionSelect slug="device-os-options" value={value.os_id} onChange={(v) => set({ os_id: v })} emptyLabel="—" />
      </Field>
      <Field label="Carrier / SIM Restriction">
        <OptionSelect
          slug="device-sim-restriction-options"
          value={value.sim_restriction_id}
          onChange={(v) => set({ sim_restriction_id: v })}
          emptyLabel="—"
        />
      </Field>
      <Field label="Jailbroken (iOS) / Rooted (Android)">
        <OptionSelect
          slug="device-root-status-options"
          value={value.root_status_id}
          onChange={(v) => set({ root_status_id: v })}
          emptyLabel="—"
        />
      </Field>
      <Field label="Device Ownership *" error={errors.ownership_id}>
        <OptionSelect slug="device-ownership-options" value={value.ownership_id} onChange={(v) => set({ ownership_id: v })} />
      </Field>
      <Field label="Device Custodian *" error={errors.custodian}>
        <OwnerSelect value={value.custodian} onChange={(v) => set({ custodian: v })} />
      </Field>
      <div className="flex gap-2">
        <Field label="Location — City" className="flex-1">
          <Input
            maxLength={DEVICE_TEXT_MAX}
            value={value.location_city}
            onChange={(e) => set({ location_city: e.target.value })}
          />
        </Field>
        <Field label="State / Region" className="flex-1">
          <Input
            maxLength={DEVICE_TEXT_MAX}
            value={value.location_region}
            onChange={(e) => set({ location_region: e.target.value })}
          />
        </Field>
      </div>
      <Field label="Device notes" className="sm:col-span-2">
        <Input value={value.notes} onChange={(e) => set({ notes: e.target.value })} />
      </Field>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Device dialog (stand-alone: device pages, OTP channel picker)
// ---------------------------------------------------------------------------

export function DeviceFormDialog({
  open,
  onClose,
  device,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** Present = edit, absent = create. */
  device?: DeviceRow;
  onSaved: (d: DeviceRow) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<DeviceDraft>(blankDevice);
  const [phoneIds, setPhoneIds] = useState<number[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  // For names and "moves from …" hints on the numbers picked here.
  const { data: phoneList } = useQuery({
    queryKey: ['phone-numbers', { active: 'all' }],
    queryFn: () => phoneNumbers.list({ active: 'all' }),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setShowErrors(false);
    setPhoneIds(device?.assigned_phones.map((p) => p.id) ?? []);
    setForm(device ? deviceDraftFrom(device) : blankDevice());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device?.id]);

  const errs = deviceDraftErrors(form);
  const shown = showErrors ? errs : {};

  const save = useMutation({
    mutationFn: () => {
      const body: DeviceUpsert = { ...deviceDraftToUpsert(form), phone_ids: phoneIds };
      return device ? devices.update(device.id, body) : devices.create(body);
    },
    onSuccess: (d) => {
      for (const key of ['devices', 'device', 'phone-numbers', 'phone-number']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
      onSaved(d);
      onClose();
    },
  });

  const phonesById = new Map((phoneList?.data ?? []).map((p) => [p.id, p]));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={device ? `Edit device ${device.device_identifier}` : 'New device'}
      className="max-w-2xl max-h-[90vh] overflow-y-auto"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={save.isPending}
            onClick={() => {
              setShowErrors(true);
              if (Object.keys(errs).length === 0) save.mutate();
            }}
          >
            {save.isPending ? 'Saving…' : 'Save device'}
          </Button>
        </>
      }
    >
      <div className="space-y-4" onKeyDown={blockEnterSubmit}>
        <DeviceFields value={form} onChange={setForm} errors={shown} />
        <div className="space-y-2 text-sm">
          <span className="block text-slate-600">Phone numbers on this device</span>
          {phoneIds.length === 0 ? (
            <p className="text-slate-500">None.</p>
          ) : (
            <ul className="space-y-1">
              {phoneIds.map((id) => {
                const p = phonesById.get(id);
                const movesFrom =
                  p && p.device_id !== null && p.device_id !== device?.id ? p.device_display : null;
                return (
                  <li key={id} className="flex items-center gap-2">
                    <span className="font-mono">{p?.display ?? `#${id}`}</span>
                    {movesFrom && (
                      <span className="text-xs text-amber-700">moves here from {movesFrom}</span>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="ml-auto"
                      onClick={() => setPhoneIds(phoneIds.filter((x) => x !== id))}
                    >
                      Remove
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
          <PhonePicker
            value={null}
            onChange={(id) => {
              if (id !== null && !phoneIds.includes(id)) setPhoneIds([...phoneIds, id]);
            }}
            placeholder="+ Add a phone number…"
            exclude={phoneIds}
          />
          <p className="text-xs text-slate-500">
            Removed numbers are left with no device. Changes apply when you save.
          </p>
        </div>
      </div>
      {save.error && <p className="mt-2 text-sm text-red-600">{errorText(save.error)}</p>}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Phone dialog (with its device)
// ---------------------------------------------------------------------------

type DeviceMode = 'none' | 'existing' | 'new';

interface PhoneDraft {
  country_code: string;
  number: string;
  subscriber: OwnerValue;
  line_type: '' | PhoneLineType;
  provider_id: number;
  carrier_type_id: number;
  payment_plan_id: number;
  payment_responsibility_id: number;
  test_status_id: number;
  usage_status_id: number;
  activation_date: string;
  notes: string;
}

function blankPhone(): PhoneDraft {
  return {
    country_code: '1',
    number: '',
    subscriber: '',
    line_type: '',
    provider_id: 0,
    carrier_type_id: 0,
    payment_plan_id: 0,
    payment_responsibility_id: 0,
    test_status_id: 0,
    usage_status_id: 0,
    activation_date: '',
    notes: '',
  };
}

function phoneDraftFrom(p: PhoneNumberRow): PhoneDraft {
  return {
    country_code: p.country_code ?? '',
    number: p.number,
    subscriber: ownerValue(p),
    line_type: p.line_type ?? '',
    provider_id: p.provider_id ?? 0,
    carrier_type_id: p.carrier_type_id ?? 0,
    payment_plan_id: p.payment_plan_id ?? 0,
    payment_responsibility_id: p.payment_responsibility_id ?? 0,
    test_status_id: p.test_status_id ?? 0,
    usage_status_id: p.usage_status_id ?? 0,
    activation_date: p.activation_date ?? '',
    notes: p.notes ?? '',
  };
}

export function PhoneFormDialog({
  open,
  onClose,
  phone,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** Present = edit, absent = create. */
  phone?: PhoneNumberRow | PhoneNumberDetail;
  onSaved: (p: { id: number }) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PhoneDraft>(blankPhone);
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('none');
  const [deviceId, setDeviceId] = useState<number | null>(null);
  const [device, setDevice] = useState<DeviceDraft>(blankDevice);
  const [deviceDirty, setDeviceDirty] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const { data: deviceList } = useQuery({
    queryKey: ['devices', { active: 'all' }],
    queryFn: () => devices.list({ active: 'all' }),
    enabled: open,
  });
  const deviceRows = (deviceList?.data ?? []).filter((d) => d.active || d.id === deviceId);

  useEffect(() => {
    if (!open) return;
    setShowErrors(false);
    setForm(phone ? phoneDraftFrom(phone) : blankPhone());
    setDeviceMode(phone?.device_id ? 'existing' : 'none');
    setDeviceId(phone?.device_id ?? null);
    setDevice(blankDevice());
    setDeviceDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone?.id]);

  // Load the chosen device's fields so they can be reviewed / edited here.
  useEffect(() => {
    if (deviceMode !== 'existing' || deviceId === null || deviceDirty) return;
    const d = deviceList?.data.find((x) => x.id === deviceId);
    if (d) setDevice(deviceDraftFrom(d));
  }, [deviceMode, deviceId, deviceList, deviceDirty]);

  const set = (patch: Partial<PhoneDraft>) => setForm({ ...form, ...patch });
  const cc = form.country_code.trim();
  const errs: Record<string, string> = {};
  if (!isValidCountryCode(cc)) errs.country_code = '1–3 digits';
  if (!isValidPhoneNumber(form.number.trim(), cc)) errs.number = 'Digits only, 4–14 digits';
  if (!form.subscriber) errs.subscriber = 'Select the subscriber';
  if (!form.line_type) errs.line_type = 'Select a line type';
  for (const [key, label] of [
    ['provider_id', 'carrier'],
    ['carrier_type_id', 'carrier type'],
    ['payment_plan_id', 'payment plan'],
    ['payment_responsibility_id', 'payment responsibility'],
    ['test_status_id', 'test status'],
    ['usage_status_id', 'usage status'],
  ] as const) {
    if (!form[key]) errs[key] = `Select the ${label}`;
  }
  if (deviceMode === 'existing' && deviceId === null) errs.device = 'Select the device';
  const deviceErrs = deviceMode === 'new' || (deviceMode === 'existing' && deviceDirty)
    ? deviceDraftErrors(device)
    : {};
  const hasErrors = Object.keys(errs).length > 0 || Object.keys(deviceErrs).length > 0;
  const shown = showErrors ? errs : {};

  const save = useMutation({
    mutationFn: async () => {
      // Device first, so the phone can point at it.
      let targetDevice: number | null = null;
      if (deviceMode === 'new') {
        targetDevice = (await devices.create(deviceDraftToUpsert(device))).id;
      } else if (deviceMode === 'existing' && deviceId !== null) {
        if (deviceDirty) await devices.update(deviceId, deviceDraftToUpsert(device));
        targetDevice = deviceId;
      }
      const body: PhoneNumberUpsert = {
        country_code: cc,
        number: form.number.trim(),
        line_type: form.line_type as PhoneLineType,
        provider_id: form.provider_id,
        carrier_type_id: form.carrier_type_id,
        payment_plan_id: form.payment_plan_id,
        payment_responsibility_id: form.payment_responsibility_id,
        test_status_id: form.test_status_id,
        usage_status_id: form.usage_status_id,
        activation_date: form.activation_date || null,
        ...ownerInput(form.subscriber),
        device_id: targetDevice,
        notes: form.notes.trim() || null,
      };
      return phone ? phoneNumbers.update(phone.id, body) : phoneNumbers.create(body);
    },
    onSuccess: (p) => {
      for (const key of ['phone-numbers', 'phone-number', 'devices', 'device']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
      onSaved(p);
      onClose();
    },
  });

  // Registering a number that already exists → offer to use that record.
  const existingId =
    save.error instanceof ApiError && save.error.status === 409
      ? (save.error.details as { existing_phone_number_id?: number } | undefined)
          ?.existing_phone_number_id
      : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={phone ? `Edit ${phone.display}` : 'New phone'}
      className="max-w-3xl max-h-[90vh] overflow-y-auto"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={save.isPending}
            onClick={() => {
              setShowErrors(true);
              if (!hasErrors) save.mutate();
            }}
          >
            {save.isPending ? 'Saving…' : 'Save phone'}
          </Button>
        </>
      }
    >
      <div className="space-y-5" onKeyDown={blockEnterSubmit}>
        <Section title="Number">
          <div className="flex gap-2 sm:col-span-2">
            <Field label="Country code *" error={shown.country_code} className="w-24">
              <div className="flex items-center">
                <span className="pr-1 text-slate-500">+</span>
                <Input
                  inputMode="numeric"
                  maxLength={3}
                  value={form.country_code}
                  onChange={(e) => set({ country_code: e.target.value })}
                  className="font-mono"
                />
              </div>
            </Field>
            <Field label="Phone Number *" error={shown.number} className="flex-1">
              <Input
                inputMode="numeric"
                maxLength={14}
                value={form.number}
                onChange={(e) => set({ number: e.target.value })}
                className="font-mono"
              />
            </Field>
          </div>
          <Field label="Subscriber Name *" error={shown.subscriber}>
            <OwnerSelect value={form.subscriber} onChange={(v) => set({ subscriber: v })} />
          </Field>
          <Field label="Line Type *" error={shown.line_type}>
            <select
              value={form.line_type}
              onChange={(e) => set({ line_type: e.target.value as '' | PhoneLineType })}
              className={selectClass}
            >
              <option value="">— select —</option>
              {PHONE_LINE_TYPES.map((lt) => (
                <option key={lt.value} value={lt.value}>
                  {lt.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Activation Date">
            <Input type="date" value={form.activation_date} onChange={(e) => set({ activation_date: e.target.value })} />
          </Field>
        </Section>

        <Section title="Carrier & payment">
          <Field label="Carrier Name *" error={shown.provider_id}>
            <OptionSelect slug="phone-provider-options" value={form.provider_id} onChange={(v) => set({ provider_id: v })} />
          </Field>
          <Field label="Carrier Type *" error={shown.carrier_type_id}>
            <OptionSelect slug="phone-carrier-type-options" value={form.carrier_type_id} onChange={(v) => set({ carrier_type_id: v })} />
          </Field>
          <Field label="Payment Plan *" error={shown.payment_plan_id}>
            <OptionSelect slug="phone-payment-plan-options" value={form.payment_plan_id} onChange={(v) => set({ payment_plan_id: v })} />
          </Field>
          <Field label="Payment Responsibility *" error={shown.payment_responsibility_id}>
            <OptionSelect
              slug="phone-payment-responsibility-options"
              value={form.payment_responsibility_id}
              onChange={(v) => set({ payment_responsibility_id: v })}
            />
          </Field>
        </Section>

        <Section title="Status">
          <Field label="Test Status *" error={shown.test_status_id}>
            <OptionSelect slug="phone-test-status-options" value={form.test_status_id} onChange={(v) => set({ test_status_id: v })} />
          </Field>
          <Field label="Usage Status *" error={shown.usage_status_id}>
            <OptionSelect slug="phone-usage-status-options" value={form.usage_status_id} onChange={(v) => set({ usage_status_id: v })} />
          </Field>
        </Section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-800">Device</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            {(
              [
                ['none', 'No device'],
                ['existing', 'Existing device'],
                ['new', 'New device'],
              ] as const
            ).map(([mode, label]) => (
              <label key={mode} className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="device-mode"
                  checked={deviceMode === mode}
                  onChange={() => {
                    setDeviceMode(mode);
                    setDeviceDirty(false);
                    setDevice(blankDevice());
                    if (mode !== 'existing') setDeviceId(null);
                  }}
                />
                {label}
              </label>
            ))}
          </div>
          {deviceMode === 'existing' && (
            <Field label="Device *" error={shown.device}>
              <select
                value={deviceId ?? ''}
                onChange={(e) => {
                  setDeviceId(e.target.value ? Number(e.target.value) : null);
                  setDeviceDirty(false);
                }}
                className={selectClass}
              >
                <option value="">— select —</option>
                {deviceRows.map((d) => (
                  <option key={d.id} value={d.id}>
                    {deviceOptionLabel(d)}
                    {d.active ? '' : ' (retired)'}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {(deviceMode === 'new' || (deviceMode === 'existing' && deviceId !== null)) && (
            <div className="rounded border border-slate-200 p-3">
              {deviceMode === 'existing' && (
                <p className="mb-2 text-xs text-slate-500">
                  Changes to these fields update the device itself (it may hold other numbers too).
                </p>
              )}
              <DeviceFields
                value={device}
                onChange={(d) => {
                  setDevice(d);
                  setDeviceDirty(true);
                }}
                errors={showErrors ? deviceErrs : {}}
              />
            </div>
          )}
        </section>

        {phone && 'ports' in phone && (
          <section className="space-y-1 text-sm">
            <h3 className="text-sm font-semibold text-slate-800">Porting history</h3>
            {phone.ports.length === 0 ? (
              <p className="text-slate-500">No ports recorded. Add one on the phone’s page.</p>
            ) : (
              <ul className="space-y-0.5">
                {phone.ports.map((p) => (
                  <li key={p.id}>
                    {p.port_date}: {p.from_carrier_name ?? '—'} → {p.to_carrier_name}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <Field label="FRT Notes">
          <textarea
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
            rows={2}
            className="block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </Field>
      </div>
      {save.error && (
        <p className="mt-2 text-sm text-red-600">
          {errorText(save.error)}
          {existingId && !phone && (
            <>
              {' '}
              <button
                type="button"
                className="text-brand-700 underline"
                onClick={() => {
                  onSaved({ id: existingId });
                  onClose();
                }}
              >
                Use the existing record
              </button>
            </>
          )}
        </p>
      )}
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Pickers (select from the registry, or add a new one in place)
// ---------------------------------------------------------------------------

export function DevicePicker({
  value,
  onChange,
  noneLabel = '— select —',
  canCreate = true,
}: {
  value: number | null;
  onChange: (id: number | null) => void;
  noneLabel?: string;
  canCreate?: boolean;
}) {
  const [creating, setCreating] = useState(false);
  const { data } = useQuery({
    queryKey: ['devices', { active: 'all' }],
    queryFn: () => devices.list({ active: 'all' }),
  });
  // Retired devices stay listed only when already selected.
  const rows = (data?.data ?? []).filter((d) => d.active || d.id === value);
  return (
    <>
      <select
        value={value ?? ''}
        onChange={(e) => {
          if (e.target.value === NEW) setCreating(true);
          else onChange(e.target.value ? Number(e.target.value) : null);
        }}
        className={selectClass}
      >
        <option value="">{noneLabel}</option>
        {rows.map((d) => (
          <option key={d.id} value={d.id}>
            {deviceOptionLabel(d)}
            {d.active ? '' : ' (retired)'}
          </option>
        ))}
        {canCreate && <option value={NEW}>+ New device…</option>}
      </select>
      <DeviceFormDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(d) => onChange(d.id)}
      />
    </>
  );
}

export function PhonePicker({
  value,
  onChange,
  canCreate = true,
  placeholder = '— select —',
  exclude = [],
}: {
  value: number | null;
  onChange: (id: number | null) => void;
  canCreate?: boolean;
  placeholder?: string;
  /** Numbers not to offer (e.g. already on the device being edited). */
  exclude?: number[];
}) {
  const [creating, setCreating] = useState(false);
  const { data } = useQuery({
    queryKey: ['phone-numbers', { active: 'all' }],
    queryFn: () => phoneNumbers.list({ active: 'all' }),
  });
  const rows = (data?.data ?? []).filter(
    (p) => (p.active || p.id === value) && !exclude.includes(p.id),
  );
  return (
    <>
      <select
        value={value ?? ''}
        onChange={(e) => {
          if (e.target.value === NEW) setCreating(true);
          else onChange(e.target.value ? Number(e.target.value) : null);
        }}
        className={`${selectClass} font-mono`}
      >
        <option value="">{placeholder}</option>
        {rows.map((p) => (
          <option key={p.id} value={p.id}>
            {p.display}
            {p.owner_display ? ` · ${p.owner_display}` : ''}
            {p.active ? '' : ' (retired)'}
          </option>
        ))}
        {canCreate && <option value={NEW}>+ New phone…</option>}
      </select>
      <PhoneFormDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(p) => onChange(p.id)}
      />
    </>
  );
}

function deviceOptionLabel(d: DeviceRow): string {
  const name = [d.make, d.model].filter(Boolean).join(' ');
  const owner = d.owner_display ? ` · ${d.owner_display}` : '';
  return `${d.device_identifier}${name ? ` — ${name}` : ''}${owner}`;
}
